<script lang="ts" setup>
import { computed } from "vue";
import type { PropType } from "vue";
import OrderLabel from "@/components/molecules/orders/OrderLabel.vue";
import SimpleOrderListItem from "@/components/molecules/orders/SimpleOrderListItem.vue";
import { OrderDetail } from "@/types/order/Order";
import { getOptimizedImageUrl, isDataUri } from "@/utils/image";
import { formatNumber } from "@/utils/number";

const props = defineProps({
  order: {
    type: Object as PropType<OrderDetail>,
    required: true
  },
  orderServiceName: {
    type: String,
    required: true
  }
});

const orderTitle = computed(() => props.order.title);
const hourlyWageLabel = computed(() => {
  const hourlyWage = props.order.hourlyWage;
  return `時給${formatNumber(hourlyWage)}円`;
});
const variousAllowancesLabel = computed(() => {
  const variousAllowances = props.order.variousAllowances;
  if (variousAllowances === 0) {
    return "";
  }
  return `交通費${formatNumber(variousAllowances)}円込`;
});

function getOptimizedOrderImage(image: string | null) {
  if (isDataUri(image)) return image || undefined;
  return getOptimizedImageUrl(image, 768, null) || undefined;
}
</script>

<template>
  <div class="order-card">
    <div class="order-card-header">
      <OrderLabel :order="order" :orderService="orderServiceName" />
    </div>
    <div class="d-flex align-items-center justify-content-between mt-1">
      <span class="order-card-title--break">{{ orderTitle }}</span>
    </div>
    <!-- カルーセル -->
    <div class="mt-2 mx-n3">
      <BCarousel
        :id="'carousel' + order.id"
        indicators
        controls
        class="order-card-carousel"
      >
        <BCarouselSlide v-if="getOptimizedOrderImage(order.image1)">
          <template #img>
            <img
              :src="getOptimizedOrderImage(order.image1)"
              class="carousel-image"
            />
          </template>
        </BCarouselSlide>
        <BCarouselSlide v-if="getOptimizedOrderImage(order.image2)">
          <template #img>
            <img
              :src="getOptimizedOrderImage(order.image2)"
              class="carousel-image"
            />
          </template>
        </BCarouselSlide>
        <BCarouselSlide v-if="getOptimizedOrderImage(order.image3)">
          <template #img>
            <img
              :src="getOptimizedOrderImage(order.image3)"
              class="carousel-image"
            />
          </template>
        </BCarouselSlide>
      </BCarousel>
    </div>

    <div class="work-time mt-2" data-testid="work-time">
      <div class="w-100 mb-2">
        <SimpleOrderListItem
          :order="order"
          :isLink="false"
          :isShownTag="false"
        />
      </div>
      <div class="reward-detail">
        <div class="ml-auto hourly-wage" data-testid="hourly-wage">
          {{ hourlyWageLabel }}
        </div>
        <div
          class="ml-auto various-allowances"
          data-testid="various-allowances"
        >
          {{ variousAllowancesLabel }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.order-card {
  padding: 12px;
  overflow: hidden;
  background-color: $white;
  border-top: 1px solid $gray-03;
  border-bottom: 1px solid $gray-03;
}

.order-card-header {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.order-card-carousel {
  .carousel-image {
    width: 100%;
    height: 300px;
    object-fit: cover;
  }
}

.order-card-title {
  margin-right: 16px;
  font-size: 18px;

  &--break {
    overflow-wrap: break-word;
  }

  &--ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.work-time {
  .reward-detail {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .hourly-wage {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      color: $mild;
    }

    .various-allowances {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      color: $mild;
    }
  }
}
</style>
